import React, { useState, useEffect, useRef } from 'react';
import { fabric } from 'fabric';

const CardConfigModal = ({ isOpen, onClose, showSuccess, showWarning, showError, frontCanvas, backCanvas, currentSide, onUpdateDimensions }) => {
    const [cardFormat, setCardFormat] = useState('standard');
    const [customWidth, setCustomWidth] = useState(85.6);
    const [customHeight, setCustomHeight] = useState(54);
    const [orientation, setOrientation] = useState('horizontal');
    const [backgroundType, setBackgroundType] = useState('color');
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [backgroundMode, setBackgroundMode] = useState('cover');
    const [backgroundPosX, setBackgroundPosX] = useState('center');
    const [backgroundPosY, setBackgroundPosY] = useState('center');
    const [applyToSide, setApplyToSide] = useState('current');
    const [previewDimensions, setPreviewDimensions] = useState({ width: 856, height: 540 });

    const cardFormats = {
        standard: { width: 856, height: 540, name: "Estándar ISO/IEC 7810 ID-1" },
        mini: { width: 700, height: 450, name: "Mini Tarjeta" },
        square: { width: 850, height: 850, name: "Tarjeta Cuadrada" },
        business: { width: 900, height: 500, name: "Tarjeta de Visita" },
        credit: { width: 856, height: 540, name: "Tarjeta de Crédito" },
        custom: { width: 856, height: 540, name: "Personalizado" }
    };

    useEffect(() => {
        updatePreview();
    }, [cardFormat, customWidth, customHeight, orientation]);

    const updatePreview = () => {
        let width, height;

        if (cardFormat === 'custom') {
            width = Math.round(customWidth * 10);
            height = Math.round(customHeight * 10);
        } else {
            const formatData = cardFormats[cardFormat];
            width = formatData.width;
            height = formatData.height;
        }

        if (orientation === 'vertical') {
            [width, height] = [height, width];
        }

        setPreviewDimensions({ width, height });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setBackgroundImage(e.target.files[0]);
        }
    };

    const isCanvasInvalid = (canvas, operation) => {
        if (!canvas || typeof canvas.getElement !== 'function' || !canvas.getElement()) {
            console.error(`❌ Canvas no disponible para ${operation}`);
            showWarning(`Error: Canvas no está disponible para ${operation}`);
            return true;
        }
        return false;
    };

    const applyBackgroundImageToCanvas = (canvas, file, config, side) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (isCanvasInvalid(canvas, `cargar imagen de fondo para ${side}`)) return;

            fabric.Image.fromURL(e.target.result, (img) => {
                if (!img || isCanvasInvalid(canvas, `aplicar imagen de fondo a ${side}`)) return;

                try {
                    img.set({
                        crossOrigin: 'anonymous',
                        selectable: false,
                        evented: false
                    });

                    const canvasWidth = canvas.getWidth ? canvas.getWidth() : canvas.width;
                    const canvasHeight = canvas.getHeight ? canvas.getHeight() : canvas.height;
                    const imgAspectRatio = img.width / img.height;
                    const canvasAspectRatio = canvasWidth / canvasHeight;

                    let scaleX = 1, scaleY = 1, left = canvasWidth / 2, top = canvasHeight / 2, originX = 'center', originY = 'center';

                    switch (config.mode) {
                        case 'cover':
                            if (imgAspectRatio > canvasAspectRatio) {
                                scaleY = canvasHeight / img.height;
                                scaleX = scaleY;
                            } else {
                                scaleX = canvasWidth / img.width;
                                scaleY = scaleX;
                            }
                            break;
                        case 'contain':
                            if (imgAspectRatio > canvasAspectRatio) {
                                scaleX = canvasWidth / img.width;
                                scaleY = scaleX;
                            } else {
                                scaleY = canvasHeight / img.height;
                                scaleX = scaleY;
                            }
                            break;
                        case 'stretch':
                            scaleX = canvasWidth / img.width;
                            scaleY = canvasHeight / img.height;
                            break;
                        case 'tile':
                            const pattern = new fabric.Pattern({ source: img.getElement(), repeat: 'repeat' });
                            canvas.setBackgroundColor(pattern, canvas.renderAll.bind(canvas));
                            canvas.setBackgroundImage(null, canvas.renderAll.bind(canvas));
                            return;
                    }

                    switch (config.posX) {
                        case 'left': left = 0; originX = 'left'; break;
                        case 'right': left = canvasWidth; originX = 'right'; break;
                        default: left = canvasWidth / 2; originX = 'center'; break;
                    }
                    switch (config.posY) {
                        case 'top': top = 0; originY = 'top'; break;
                        case 'bottom': top = canvasHeight; originY = 'bottom'; break;
                        default: top = canvasHeight / 2; originY = 'center'; break;
                    }

                    canvas.setBackgroundColor(null, canvas.renderAll.bind(canvas));
                    canvas.setBackgroundImage(img, () => {
                        try {
                            canvas.renderAll();
                        } catch { }
                    }, { scaleX, scaleY, left, top, originX, originY });
                } catch (setBackgroundError) {
                    console.error('❌ Error estableciendo imagen de fondo:', setBackgroundError);
                }
            }, { crossOrigin: 'anonymous' });
        };
        reader.readAsDataURL(file);
    };

    const handleApplyConfig = () => {
        const applyToCanvas = (canvas, side) => {
            if (!canvas) return;

            if (backgroundType === 'color') {
                if (isCanvasInvalid(canvas, `aplicar color de fondo a ${side}`)) return;
                try {
                    canvas.setBackgroundImage(null, canvas.renderAll.bind(canvas));
                    canvas.setBackgroundColor(backgroundColor, () => {
                        try { canvas.renderAll(); } catch { }
                    });
                } catch { }
            } else {
                if (backgroundImage) {
                    const imageConfig = {
                        mode: backgroundMode,
                        posX: backgroundPosX,
                        posY: backgroundPosY
                    };
                    applyBackgroundImageToCanvas(canvas, backgroundImage, imageConfig, side);
                } else {
                    showWarning(`No se seleccionó imagen para el lado ${side}.`);
                }
            }
        };

        const canvasesToUpdate = [];
        if (applyToSide === 'current') {
            canvasesToUpdate.push({ canvas: currentSide === 'front' ? frontCanvas : backCanvas, side: currentSide });
        } else if (applyToSide === 'both') {
            canvasesToUpdate.push({ canvas: frontCanvas, side: 'front' });
            if (backCanvas) canvasesToUpdate.push({ canvas: backCanvas, side: 'back' });
        } else if (applyToSide === 'front') {
            canvasesToUpdate.push({ canvas: frontCanvas, side: 'front' });
        } else if (applyToSide === 'back') {
            if (backCanvas) canvasesToUpdate.push({ canvas: backCanvas, side: 'back' });
        }

        canvasesToUpdate.forEach(({ canvas, side }) => applyToCanvas(canvas, side));

        // Aplicar dimensiones
        const { width, height } = previewDimensions;

        if (onUpdateDimensions) {
            onUpdateDimensions({ width, height });
        }

        const activeCanvas = currentSide === 'front' ? frontCanvas : (backCanvas || frontCanvas);

        if (activeCanvas) {
            try {
                if (isCanvasInvalid(activeCanvas, 'redimensionar canvas activo')) return;

                const prevWidth = activeCanvas.width;
                const prevHeight = activeCanvas.height;
                const scaleX = width / prevWidth;
                const scaleY = height / prevHeight;

                activeCanvas.getObjects().forEach(obj => {
                    if (!obj.excludeFromExport) {
                        obj.scaleX = (obj.scaleX || 1) * scaleX;
                        obj.scaleY = (obj.scaleY || 1) * scaleY;
                        obj.left = (obj.left || 0) * scaleX;
                        obj.top = (obj.top || 0) * scaleY;
                        obj.setCoords && obj.setCoords();
                    }
                });

                activeCanvas.setDimensions({ width, height });
                activeCanvas.renderAll();

                if (backCanvas && currentSide === 'front') {
                    if (isCanvasInvalid(backCanvas, 'redimensionar canvas de reverso')) return;

                    backCanvas.getObjects().forEach(obj => {
                        if (!obj.excludeFromExport) {
                            obj.scaleX = (obj.scaleX || 1) * scaleX;
                            obj.scaleY = (obj.scaleY || 1) * scaleY;
                            obj.left = (obj.left || 0) * scaleX;
                            obj.top = (obj.top || 0) * scaleY;
                            obj.setCoords && obj.setCoords();
                        }
                    });
                    backCanvas.setDimensions({ width, height });
                    backCanvas.renderAll();
                }
            } catch (error) {
                showError('Error al aplicar la configuración al canvas');
                return;
            }
        } else {
            showError('No hay canvas activo para aplicar la configuración');
            return;
        }

        showSuccess(`✅ Configuración aplicada correctamente`);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-sans">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <span className="text-2xl mr-3">🎯</span>
                        <h3 className="text-lg font-semibold text-white">Configurar Tarjetas de Plástico</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Formato de tarjeta */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Formato de Tarjeta:</label>
                        <select
                            value={cardFormat}
                            onChange={(e) => setCardFormat(e.target.value)}
                            className="w-full border border-slate-600 rounded-md px-3 py-2 bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                            {Object.entries(cardFormats).map(([key, value]) => (
                                <option key={key} value={key}>{value.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Dimensiones personalizadas */}
                    {cardFormat === 'custom' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Ancho (mm):</label>
                                <input
                                    type="number"
                                    min="20"
                                    max="150"
                                    step="0.1"
                                    value={customWidth}
                                    onChange={(e) => setCustomWidth(parseFloat(e.target.value))}
                                    className="w-full border border-slate-600 rounded-md px-3 py-2 bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">Alto (mm):</label>
                                <input
                                    type="number"
                                    min="15"
                                    max="100"
                                    step="0.1"
                                    value={customHeight}
                                    onChange={(e) => setCustomHeight(parseFloat(e.target.value))}
                                    className="w-full border border-slate-600 rounded-md px-3 py-2 bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Orientación */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Orientación:</label>
                        <div className="grid grid-cols-2 gap-4">
                            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${orientation === 'horizontal' ? 'bg-slate-800 border-blue-500' : 'bg-slate-800/50 border-slate-600 hover:bg-slate-800'}`}>
                                <input
                                    type="radio"
                                    name="orientation"
                                    value="horizontal"
                                    checked={orientation === 'horizontal'}
                                    onChange={() => setOrientation('horizontal')}
                                    className="mr-3 text-blue-600 focus:ring-blue-500 bg-slate-700 border-slate-500"
                                />
                                <div className="text-center flex-1">
                                    <div className="text-2xl mb-1">📱</div>
                                    <div className="text-sm font-medium text-slate-200">Horizontal</div>
                                    <div className="text-xs text-slate-400">Paisaje (85.6 × 54 mm)</div>
                                </div>
                            </label>
                            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${orientation === 'vertical' ? 'bg-slate-800 border-blue-500' : 'bg-slate-800/50 border-slate-600 hover:bg-slate-800'}`}>
                                <input
                                    type="radio"
                                    name="orientation"
                                    value="vertical"
                                    checked={orientation === 'vertical'}
                                    onChange={() => setOrientation('vertical')}
                                    className="mr-3 text-blue-600 focus:ring-blue-500 bg-slate-700 border-slate-500"
                                />
                                <div className="text-center flex-1">
                                    <div className="text-2xl mb-1">📲</div>
                                    <div className="text-sm font-medium text-slate-200">Vertical</div>
                                    <div className="text-xs text-slate-400">Retrato (54 × 85.6 mm)</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Color de fondo */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Fondo:</label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${backgroundType === 'color' ? 'bg-slate-800 border-blue-500' : 'bg-slate-800/50 border-slate-600 hover:bg-slate-800'}`}>
                                <input
                                    type="radio"
                                    name="backgroundType"
                                    value="color"
                                    checked={backgroundType === 'color'}
                                    onChange={() => setBackgroundType('color')}
                                    className="mr-3 text-blue-600 focus:ring-blue-500 bg-slate-700 border-slate-500"
                                />
                                <div className="text-center">
                                    <div className="text-xl mb-1">🎨</div>
                                    <div className="text-sm font-medium text-slate-200">Color Sólido</div>
                                </div>
                            </label>
                            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${backgroundType === 'image' ? 'bg-slate-800 border-blue-500' : 'bg-slate-800/50 border-slate-600 hover:bg-slate-800'}`}>
                                <input
                                    type="radio"
                                    name="backgroundType"
                                    value="image"
                                    checked={backgroundType === 'image'}
                                    onChange={() => setBackgroundType('image')}
                                    className="mr-3 text-blue-600 focus:ring-blue-500 bg-slate-700 border-slate-500"
                                />
                                <div className="text-center">
                                    <div className="text-xl mb-1">🖼️</div>
                                    <div className="text-sm font-medium text-slate-200">Imagen</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {backgroundType === 'color' ? (
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Color de Fondo:</label>
                            <div className="flex items-center space-x-4">
                                <input
                                    type="color"
                                    value={backgroundColor}
                                    onChange={(e) => setBackgroundColor(e.target.value)}
                                    className="w-16 h-10 border border-slate-600 rounded bg-slate-800 cursor-pointer"
                                />
                                <span className="text-sm text-slate-400">Selecciona el color de fondo del canvas</span>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">Imagen de Fondo:</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                            />

                            {backgroundImage && (
                                <div className="mt-4 p-4 bg-slate-800 rounded-lg border border-slate-700 animate-fade-in-up">
                                    <h4 className="font-medium text-slate-200 mb-3">Configuración de imagen:</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm text-slate-400 mb-1">Modo de aplicación:</label>
                                            <select value={backgroundMode} onChange={(e) => setBackgroundMode(e.target.value)} className="w-full border border-slate-600 rounded px-3 py-2 text-sm bg-slate-700 text-white focus:ring-2 focus:ring-blue-500 outline-none">
                                                <option value="cover">Cubrir área completa (recomendado)</option>
                                                <option value="contain">Ajustar dentro del área</option>
                                                <option value="stretch">Estirar para llenar</option>
                                                <option value="tile">Repetir como patrón</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">Posición X:</label>
                                                <select value={backgroundPosX} onChange={(e) => setBackgroundPosX(e.target.value)} className="w-full border border-slate-600 rounded px-2 py-1 text-sm bg-slate-700 text-white focus:ring-2 focus:ring-blue-500 outline-none">
                                                    <option value="left">Izquierda</option>
                                                    <option value="center">Centro</option>
                                                    <option value="right">Derecha</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">Posición Y:</label>
                                                <select value={backgroundPosY} onChange={(e) => setBackgroundPosY(e.target.value)} className="w-full border border-slate-600 rounded px-2 py-1 text-sm bg-slate-700 text-white focus:ring-2 focus:ring-blue-500 outline-none">
                                                    <option value="top">Arriba</option>
                                                    <option value="center">Centro</option>
                                                    <option value="bottom">Abajo</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Lado a aplicar */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Aplicar a:</label>
                        <select
                            value={applyToSide}
                            onChange={(e) => setApplyToSide(e.target.value)}
                            className="w-full border border-slate-600 rounded-md px-3 py-2 bg-slate-800 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                            <option value="current">Lado Actual ({currentSide === 'front' ? 'Frente' : 'Reverso'})</option>
                            <option value="both">Ambos Lados</option>
                            <option value="front">Solo Frente</option>
                            <option value="back">Solo Reverso</option>
                        </select>
                    </div>

                    {/* Opciones de Codificación */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Opciones de Codificación:</label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center space-x-3 p-3 border border-slate-700 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-slate-500 focus:ring-blue-500 bg-slate-700" />
                                <span className="text-sm text-slate-200">Banda Magnética</span>
                            </label>
                            <label className="flex items-center space-x-3 p-3 border border-slate-700 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-slate-500 focus:ring-blue-500 bg-slate-700" />
                                <span className="text-sm text-slate-200">Chip de Contacto</span>
                            </label>
                            <label className="flex items-center space-x-3 p-3 border border-slate-700 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-slate-500 focus:ring-blue-500 bg-slate-700" />
                                <span className="text-sm text-slate-200">Contactless (RFID)</span>
                            </label>
                            <label className="flex items-center space-x-3 p-3 border border-slate-700 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-slate-500 focus:ring-blue-500 bg-slate-700" />
                                <span className="text-sm text-slate-200">NFC</span>
                            </label>
                        </div>
                    </div>

                    {/* Preview de dimensiones */}
                    <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
                        <h4 className="font-medium text-blue-400 mb-2">📏 Dimensiones Finales:</h4>
                        <div className="text-sm text-blue-300">
                            <div><strong>Formato:</strong> {cardFormat === 'custom' ? 'Personalizado' : cardFormats[cardFormat]?.name}</div>
                            <div><strong>Dimensiones:</strong> {previewDimensions.width / 10} × {previewDimensions.height / 10} mm</div>
                            <div><strong>Píxeles:</strong> {previewDimensions.width} × {previewDimensions.height} px</div>
                            <div><strong>Orientación:</strong> {orientation === 'horizontal' ? 'Horizontal (Paisaje)' : 'Vertical (Retrato)'}</div>
                        </div>
                    </div>
                </div>

                <div className="flex space-x-3 mt-6">
                    <button onClick={onClose} className="flex-1 px-4 py-2 text-slate-300 border border-slate-600 rounded-lg hover:bg-slate-800 hover:text-white transition-colors">
                        Cancelar
                    </button>
                    <button
                        onClick={handleApplyConfig}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-900/50 transition-all font-medium"
                    >
                        ✅ Aplicar Configuración
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CardConfigModal;
